// src/utils/pdfText.js
//
// Shared pdf-lib text helpers for the FIG document generators.
//
// This module exists because toRgb was duplicated identically in
// invoicePdf.js and letterheadPdf.js, and because letterheadPdf.js's
// wrapText measured with widthOfTextAtSize — which both ignores the
// inter-glyph tracking advance and measures on the wrong basis entirely
// (see the kerning note on measureTracked below).
//
// Neither invoicePdf.js nor letterheadPdf.js is modified by this file.
// Migrating them to these helpers is a separate change.

import { rgb } from 'pdf-lib';

const EM_DASH = '—';

/**
 * Slack allowed before a draw counts as overrunning its column. Right-aligned
 * text lands on the edge exactly; this only absorbs float noise.
 */
const OVERFLOW_TOLERANCE = 0.01;

// NAMING: this module is pdf text primitives GENERALLY — measurement, drawing,
// wrapping, case transforms — plus colour and money formatting. toRgb,
// hexToRgb and applyTransform all live here because they are cross-cutting
// helpers with no better home, not because they are about text layout
// specifically. The name pdfText is too narrow for what it holds. Rename is
// deferred to the session that backports the kerning fix into invoicePdf.js and
// letterheadPdf.js — those files get touched then anyway, so the import churn
// is free at that point instead of being a standalone rename commit.

/**
 * Resolve a { r, g, b } color (0-1 range) to a pdf-lib rgb(), falling back
 * when the primary value is missing.
 *
 * Moved verbatim from letterheadPdf.js:354 (identical copy at invoicePdf.js:385).
 */
export function toRgb(c, fallback) {
  const v = c || fallback;
  return rgb(v.r, v.g, v.b);
}

/**
 * Parse a hex color string to { r, g, b } in 0-1 range.
 *
 * Moved verbatim from brandAssets.js, which had the only copy until
 * proposalLayout.js needed one too.
 *
 * Returns null when the value is not exactly 6 characters after stripping a
 * leading '#'. That null is load-bearing: brandAssets.js and
 * proposalLayout.js both rely on it for `hexToRgb(x) || FALLBACK` chains, so a
 * bad or missing FIG_Settings hex falls through to the fallback instead of
 * producing NaN channels. Do not "improve" this to throw or to coerce.
 *
 * 3-char shorthand ('#FFF') is deliberately unsupported — it returns null.
 *
 * @param {string} hex e.g. '#660099' or '660099'
 * @returns {{r: number, g: number, b: number}|null}
 */
export function hexToRgb(hex) {
  const h = (hex || '').replace('#', '');
  if (h.length !== 6) return null;
  return {
    r: parseInt(h.slice(0, 2), 16) / 255,
    g: parseInt(h.slice(2, 4), 16) / 255,
    b: parseInt(h.slice(4, 6), 16) / 255,
  };
}

/**
 * Format a currency amount for a client-facing document.
 *
 * Deliberately unlike invoicePdf.js's private formatCurrency, which does
 * `Number(amount) || 0` and therefore prints a missing premium as $0.00.
 * Absent data renders as an em dash; only a real numeric zero is $0.00.
 *
 * @param {number|string|null|undefined} n
 * @returns {string} '$1,234,567.50', '$0.00', or '—'
 */
export function formatMoney(n) {
  if (n === null || n === undefined) return EM_DASH;
  // Blank and whitespace-only strings are absent data, not zero. Guarding
  // whitespace too, because Number(' ') is 0 and would print $0.00.
  if (typeof n === 'string' && n.trim() === '') return EM_DASH;

  const num = typeof n === 'number' ? n : Number(n);
  if (!Number.isFinite(num)) return EM_DASH;

  return '$' + num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Title Case for header cells (§1.3, "Table header cell ... Title Case").
 *
 * Relocated here from proposalTable.js: case transforms are a general
 * typographic concern, not a table one.
 *
 * DEVIATION, deliberate: tokens that are entirely uppercase are preserved
 * rather than title-cased. Strict Title Case would render TIV as "Tiv", SOV as
 * "Sov" and UM/UIM as "Um/uim". Insurance column headers are acronym-dense, so
 * mangling them is the worse failure. The cost is that an all-caps multi-word
 * header like 'TOTAL PREMIUM' is left as-is instead of becoming 'Total
 * Premium'. Flip the guard if you want strict spec behavior.
 */
export function toTitleCase(text) {
  return String(text)
    .split(/(\s+)/)
    .map((tok) => {
      if (tok === '' || /^\s+$/.test(tok)) return tok;
      const hasLower = /[a-z]/.test(tok);
      if (!hasLower && /[A-Z]/.test(tok)) return tok; // acronym — leave alone
      return tok.charAt(0).toUpperCase() + tok.slice(1).toLowerCase();
    })
    .join('');
}

/**
 * Apply a TYPE entry's `transform` ('upper' | 'title' | 'asis').
 * proposalLayout stores the transform as data and leaves applying it to callers.
 */
export function applyTransform(text, transform) {
  const s = String(text);
  if (transform === 'upper') return s.toUpperCase();
  if (transform === 'title') return toTitleCase(s);
  return s;
}

/**
 * Width of `text` as it will actually render, including letter tracking.
 *
 * DO NOT "optimize" the per-character loop into a single
 * font.widthOfTextAtSize(text, size) call. Verified against pdf-lib 1.17.1:
 * drawText emits a plain Tj with no kern offsets, so the PDF viewer advances
 * by unkerned per-character widths — but widthOfTextAtSize DOES subtract
 * kerning. pdf-lib therefore measures kerned and renders unkerned, and the
 * whole-string measure under-reports true rendered width (by 1.785 pt on
 * 'Policy Type' at HelveticaBold 8.5). Using it for alignment pushes
 * right-aligned and centered text off its intended edge. Summing per
 * character is what the renderer does, so it is what we measure.
 *
 * Tracking is in points and is added *between* glyphs only — n glyphs get
 * n-1 gaps, never a trailing one. A single character and an empty string
 * therefore get no tracking at all.
 *
 * @param {Object} font pdf-lib embedded font
 * @param {string} text
 * @param {number} size
 * @param {number} [tracking=0] points between glyphs
 * @returns {number}
 */
export function measureTracked(font, text, size, tracking = 0) {
  const t = text === null || text === undefined ? '' : String(text);
  let width = 0;
  for (const ch of t) width += font.widthOfTextAtSize(ch, size);
  return width + tracking * Math.max(0, t.length - 1);
}

/**
 * Draw text with optional letter tracking and alignment.
 *
 * With tracking 0 this is a single drawText call for every alignment — not
 * just 'left'. That is safe because the Tj drawText emits advances by
 * unkerned per-character widths, which is exactly what measureTracked now
 * returns: the fast path and the glyph loop lay out identically, so the
 * single call is just the cheaper way to get the same geometry.
 *
 * @param {Object} page pdf-lib page
 * @param {string} text
 * @param {Object} opts
 * @param {number} opts.x left edge, right edge, or center per `align`
 * @param {number} opts.y baseline
 * @param {Object} opts.font
 * @param {number} opts.size
 * @param {Object} [opts.color]
 * @param {number} [opts.tracking=0]
 * @param {'left'|'right'|'center'} [opts.align='left']
 * @param {Object} [opts.overflow] horizontal overrun detection. drawText has no
 *   maxWidth and never wraps, so a string wider than the column simply runs
 *   off the page edge and no layer below this one can tell. When supplied,
 *   any draw whose right edge passes `rightEdge` is recorded on `sink` as
 *   { part, text, overrunPt }. Detection only — the text is still drawn.
 * @param {number} opts.overflow.rightEdge
 * @param {Array} opts.overflow.sink
 * @param {string|null} [opts.overflow.part]
 * @returns {number} total advance width, equal to measureTracked for the same args
 */
export function drawTracked(page, text, {
  x, y, font, size, color, tracking = 0, align = 'left', overflow,
}) {
  const t = text === null || text === undefined ? '' : String(text);
  const width = measureTracked(font, t, size, tracking);

  let startX = x;
  if (align === 'right') startX = x - width;
  else if (align === 'center') startX = x - width / 2;

  if (overflow && Array.isArray(overflow.sink)) {
    const overrun = startX + width - overflow.rightEdge;
    if (overrun > OVERFLOW_TOLERANCE) {
      overflow.sink.push({
        part: overflow.part === undefined ? null : overflow.part,
        text: t,
        overrunPt: +overrun.toFixed(2),
      });
    }
  }

  if (tracking === 0) {
    page.drawText(t, { x: startX, y, font, size, color });
    return width;
  }

  let penX = startX;
  for (const ch of t) {
    page.drawText(ch, { x: penX, y, font, size, color });
    penX += font.widthOfTextAtSize(ch, size) + tracking;
  }

  return width;
}

/**
 * Greedy width-based word wrap that accounts for tracking.
 *
 * Same algorithm as letterheadPdf.js wrapText:374, measuring with
 * measureTracked instead of widthOfTextAtSize so that tracked section
 * headings and table titles wrap at the width they actually occupy.
 * Returns [''] for empty input, matching the existing wrapper.
 *
 * @param {Object} font
 * @param {string} text
 * @param {number} size
 * @param {number} maxWidth
 * @param {number} [tracking=0]
 * @returns {string[]}
 */
export function wrapTracked(font, text, size, maxWidth, tracking = 0) {
  const words = String(text).split(/\s+/).filter(Boolean);
  const lines = [];
  let cur = '';
  for (const w of words) {
    const test = cur ? `${cur} ${w}` : w;
    if (cur && measureTracked(font, test, size, tracking) > maxWidth) {
      lines.push(cur);
      cur = w;
    } else {
      cur = test;
    }
  }
  if (cur) lines.push(cur);
  return lines.length ? lines : [''];
}
